import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Chip, Alert, CircularProgress,
  List, ListItemButton, ListItemText, Divider,
} from '@mui/material';
import venueWineRequestService from '../../services/venueWineRequestService';
import LabelPhotoCapture, {
  HEIC_GUIDANCE, PHOTO_CAP, PHOTO_MAX_BYTES, isUnsupportedHeic,
  type CapturedPhoto,
} from './LabelPhotoCapture';
import wineService, { parseVintageInput, Wine, WineSearchResult } from '../../services/wineService';
import type { WineColor } from '../../services/wineNameService';

/**
 * SIMPLE-WINE.1 — the primary way a venue adds a wine.
 *
 * Three fields: the name as it reads on the label, the vintage, and the
 * colour. Producer and region are not asked for, because asking for them is
 * what stopped people entering wines at all — the record is created
 * provisional and a reviewer supplies the identity later.
 *
 * The full structured form is not removed. It is one tap away behind "Enter
 * full details", and its behaviour is unchanged.
 */

export const WINE_COLORS: WineColor[] = [
  'RED', 'WHITE', 'ROSE', 'ORANGE', 'SPARKLING', 'DESSERT', 'FORTIFIED',
];

const COLOR_LABELS: Record<WineColor, string> = {
  RED: 'Red',
  WHITE: 'White',
  ROSE: 'Rosé',
  ORANGE: 'Orange',
  SPARKLING: 'Sparkling',
  DESSERT: 'Dessert',
  FORTIFIED: 'Fortified',
};

export const NAME_GUIDE = 'Type the wine as on the label — producer, appellation, grape (if on the label), cuvée — in one line.';

interface Props {
  open: boolean;
  venueId: string;
  initialQuery: string;
  onCreated: (wine: Wine) => void;
  /** Hand an existing wine back to the picker — the cheapest correction. */
  onSelectExisting: (wine: WineSearchResult) => void;
  /** Switch to the structured form, carrying the typed text across. */
  onEnterFullDetails: (query: string) => void;
  onClose: () => void;
}

const SimpleWineCreateDialog: React.FC<Props> = ({
  open, venueId, initialQuery, onCreated, onSelectExisting, onEnterFullDetails, onClose,
}) => {
  const [name, setName] = useState(initialQuery);
  const [vintageInput, setVintageInput] = useState('');
  const [color, setColor] = useState<WineColor | ''>('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [suggestions, setSuggestions] = useState<WineSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  // EVIDENCE.1a — optional label photographs. Held locally until the wine
  // exists, then attached; see handleSubmit for why that order matters.
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Re-arm on each open rather than on mount: the dialog stays mounted and
  // the query changes between openings.
  useEffect(() => {
    if (!open) return;
    setName(initialQuery);
    setVintageInput('');
    setColor('');
    setTouched(false);
    setSaving(false);
    setSubmitError('');
    setSuggestions([]);
    setPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setPhotoError(null);
  }, [open, initialQuery]);

  // ── Did-you-mean ───────────────────────────────────────────────────────────
  // Debounced, and deliberately advisory: results are offered, never applied.
  // Auto-selecting a close match is how the wrong wine silently enters
  // inventory.
  const seq = useRef(0);
  useEffect(() => {
    if (!open) return undefined;
    const q = name.trim();
    if (q.length < 3) { setSuggestions([]); return undefined; }

    const mine = ++seq.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await wineService.search(q, 5, venueId);
        // A stale response must not overwrite a newer one.
        if (mine === seq.current) setSuggestions(results);
      } catch {
        if (mine === seq.current) setSuggestions([]);
      } finally {
        if (mine === seq.current) setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [name, venueId, open]);

  const handleAddPhoto = useCallback((file: File) => {
    setPhotoError(null);
    // The same two refusals the server makes, made here so the person holding
    // the bottle hears them immediately rather than after an upload.
    if (isUnsupportedHeic(file)) { setPhotoError(HEIC_GUIDANCE); return; }
    if (file.size > PHOTO_MAX_BYTES) {
      setPhotoError('That photo is over 10 MB. Try again with a smaller one.');
      return;
    }
    setPhotos((prev) => (prev.length >= PHOTO_CAP
      ? prev
      : [...prev, { file, previewUrl: URL.createObjectURL(file) }]));
  }, []);

  const handleRemovePhoto = useCallback((index: number) => {
    setPhotos((prev) => {
      const gone = prev[index];
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const parsedVintage = useMemo(
    () => (vintageInput.trim() ? parseVintageInput(vintageInput) : null),
    [vintageInput],
  );

  const nameError = touched && name.trim().length < 2;
  const vintageError = touched && (!parsedVintage || !parsedVintage.valid);
  const colorError = touched && !color;
  const canSubmit = name.trim().length >= 2 && !!parsedVintage?.valid && !!color;

  const handleSubmit = useCallback(async () => {
    setTouched(true);
    if (!canSubmit || !parsedVintage) return;

    setSaving(true);
    setSubmitError('');
    try {
      const wine = await venueWineRequestService.requestNew(venueId, {
        provisionalName: name.trim(),
        color: color as WineColor,
        vintageType: parsedVintage.vintageType,
        ...(parsedVintage.vintage !== undefined ? { vintage: parsedVintage.vintage } : {}),
      });

      // EVIDENCE.1a — attach SECOND, and never let it cost the wine.
      //
      // The alternative (create-with-photo atomically) trades a common,
      // recoverable failure for an uncommon, unrecoverable one. If the upload
      // fails the operator leaves with the wine they came for and a note that
      // the photo did not attach.
      const wineNameId = (wine as { wineName?: { _id?: string } | string })?.wineName;
      const subjectId = typeof wineNameId === 'string' ? wineNameId : wineNameId?._id;
      if (photos.length > 0 && subjectId) {
        try {
          await venueWineRequestService.attachPhotos(
            venueId, subjectId, photos.map((p) => p.file),
          );
        } catch {
          setPhotoError(
            'The wine was added, but the photo could not be attached. '
            + 'You can add it from the item later.',
          );
        }
      }

      onCreated(wine);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'COMPANY_UNRESOLVED') {
        setSubmitError('Could not reach your venue’s account just now. Try again in a moment.');
      } else {
        setSubmitError(err?.response?.data?.message || 'Could not add the wine. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }, [canSubmit, parsedVintage, venueId, name, color, onCreated, photos]);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Add a wine</DialogTitle>

      <DialogContent dividers>
        {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          autoFocus
          error={nameError}
          helperText={nameError ? 'Enter the wine’s name' : NAME_GUIDE}
          inputProps={{ maxLength: 200, 'aria-label': 'Wine name' }}
        />

        {/* Did-you-mean. Offered under the field, at the moment the mistake
            would otherwise be made — not reported afterwards. */}
        {(searching || suggestions.length > 0) && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {searching ? 'Checking existing wines…' : 'Already in the catalogue — tap to use it instead:'}
            </Typography>
            {suggestions.length > 0 && (
              <List dense disablePadding sx={{ mt: 0.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {suggestions.map((s, i) => (
                  <React.Fragment key={s._id}>
                    {i > 0 && <Divider component="li" />}
                    <ListItemButton onClick={() => onSelectExisting(s)}>
                      <ListItemText
                        primary={s.name}
                        secondary={[s.displayVintage, s.wineDisplay?.regionName]
                          .filter(Boolean).join(' · ') || undefined}
                      />
                      <Chip size="small" label="Use this" />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}

        <TextField
          label="Vintage"
          value={vintageInput}
          onChange={(e) => setVintageInput(e.target.value)}
          fullWidth
          sx={{ mt: 2.5 }}
          error={vintageError}
          helperText={
            vintageError
              ? (parsedVintage?.error || 'Enter a year, or NV')
              : 'A year (e.g. 2019), or NV for non-vintage'
          }
          inputProps={{ 'aria-label': 'Vintage' }}
        />

        <Box sx={{ mt: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Colour
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {WINE_COLORS.map((c) => (
              <Chip
                key={c}
                label={COLOR_LABELS[c]}
                onClick={() => setColor(c)}
                color={color === c ? 'primary' : 'default'}
                variant={color === c ? 'filled' : 'outlined'}
                aria-pressed={color === c}
                sx={{ px: 0.5 }}
              />
            ))}
          </Box>
          {colorError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              Choose a colour
            </Typography>
          )}
        </Box>

        <LabelPhotoCapture
          photos={photos}
          onAdd={handleAddPhoto}
          onRemove={handleRemovePhoto}
          disabled={saving}
          error={photoError}
        />

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2.5, display: 'block' }}>
          The wine is added straight away and goes to the wine team for
          checking. You can use it now.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={() => onEnterFullDetails(name.trim())}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          Enter full details
        </Button>
        <Box>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{ ml: 1, textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? <CircularProgress size={22} /> : 'Add wine'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SimpleWineCreateDialog;
